import React, { useEffect } from 'react';
import { BrowserRouter as Router, useLocation, matchPath } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './Store';
import 'bootstrap/dist/css/bootstrap.min.css';
import './CSS/index.css';
import './CSS/custom.css';
import NavigationBar from './Components/NavigationBar.jsx';
import AppContent from './Components/AppContent.jsx';
import Footer from './Components/Footer.jsx';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { processQueue, hasPendingRequests } from './utils/offlineQueue';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import localDB from './services/localData'; // Import your localDB

const Layout = () => {
  const location = useLocation();
  
  const noHeaderPaths = ['/', '/signin', '/signup', '/reset-password/:token'];
  const hideHeader = noHeaderPaths.some(pattern => 
    matchPath(pattern, location.pathname)
  );

  useEffect(() => {
    const handleOnline = async () => {
      if (await hasPendingRequests()) {
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
          const reg = await navigator.serviceWorker.ready;
          reg.sync.register('sync-queued-requests');
          toast.info('Syncing your offline changes...');
        } else {
          await processQueue();
          toast.success('Offline changes synced!');
        }
      }
    };

    const handleOffline = () => {
      toast.warn('You are offline. Changes will be saved locally.');
    };

    // Add client-side sync listener
    const handleSyncMessage = async (event) => {
      if (event.data.type === 'SYNC_SUCCESS') {
        const { entityType, tempId, serverId, data } = event.data.payload;
        
        try {
          // Update local DB with server response
          switch (entityType) {
            case 'student':
              await localDB.students.delete(tempId);
              await localDB.students.add({
                ...data,
                syncStatus: 'synced'
              });
              break;
            case 'staff':
              await localDB.staff.delete(tempId);
              await localDB.staff.add({
                ...data,
                syncStatus: 'synced'
              });
              break;
            // Add other entity types as needed
          }
          toast.success(`${entityType} data synced successfully!`);
        } catch (error) {
          console.error('Failed to update local data after sync:', error);
          toast.error(`Failed to sync ${entityType} data`);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSyncMessage);
    }

    // Initial check
    if (!navigator.onLine) handleOffline();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSyncMessage);
      }
    };
  }, []);

  return (
    <>
      <ToastContainer position="bottom-right" autoClose={5000} />
      {!hideHeader && <NavigationBar />}
      <AppContent />
      <Footer />
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <Router>
        <Layout />
      </Router>
    </Provider>
  );
}

export default App;